import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Archive, Plus } from 'lucide-react';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTranscriptionList, useArchiveTranscription } from '../hooks/useTranscription.js';
import type { TranscriptionSourceType, TranscriptionStatus } from '../types/transcription.js';

/** @graph-connects none */
const SOURCE_LABEL_KEYS: Record<TranscriptionSourceType, string> = {
  drive_video: 'transcription.source.driveVideo',
  upload: 'transcription.source.upload',
  ai_fassy_import: 'transcription.source.aiFassy',
  google_docs: 'transcription.source.googleDocs',
};

/** @graph-connects none */
const STATUS_CONFIG: Record<TranscriptionStatus, { labelKey: string; color: string }> = {
  pending: { labelKey: 'transcription.status.pending', color: 'var(--color-warning)' },
  processing: { labelKey: 'transcription.status.processing', color: 'var(--color-warning)' },
  completed: { labelKey: 'transcription.status.completed', color: 'var(--color-success)' },
  failed: { labelKey: 'transcription.status.failed', color: 'var(--color-error)' },
};

/**
 * @graph-connects none
 */
function toJstDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こし一覧ページ。作成日降順でタイトル・ソース種別・ステータスを表示する。
 * @graph-connects useTranscriptionList [calls] 一覧取得hook
 */
export function TranscriptionListPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useTranscriptionList();
  const archiveMutation = useArchiveTranscription();
  const transcriptions = data?.transcriptions ?? [];

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-10">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
            Transcription
          </div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('transcription.title')}
          </h1>
        </div>
        <Link to="/transcriptions/new">
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}>
            {t('common.createNew')}
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <Card className="p-10 text-center">
          <div className="loading-spinner mx-auto mb-3" />
          <p className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
        </Card>
      ) : error ? (
        <Card className="p-10 text-center">
          <p className="text-sm text-[var(--color-error)]">
            {t('common.fetchError')}:{' '}
            {error instanceof Error ? error.message : t('common.unknownError')}
          </p>
        </Card>
      ) : transcriptions.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="mb-4 text-sm text-[var(--color-text-muted)]">{t('transcription.noData')}</p>
          <Link to="/transcriptions/new">
            <Button variant="primary" leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}>
              {t('common.createNew')}
            </Button>
          </Link>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)]">
                  {t('common.title')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)]">
                  {t('transcription.sourceColumn')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)]">
                  {t('common.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)]">
                  {t('common.createdAt')}
                </th>
                <th className="w-10 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {transcriptions.map((item) => {
                const statusConf = STATUS_CONFIG[item.status];
                return (
                  <tr
                    key={item.id}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to="/transcriptions/$id"
                        params={{ id: item.id }}
                        className="text-sm font-medium text-[var(--color-text-primary)] hover:text-[var(--color-accent-primary)]"
                      >
                        {item.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-[var(--color-bg-hover)] px-2 py-0.5 text-xs font-medium text-[var(--color-text-secondary)]">
                        {t(SOURCE_LABEL_KEYS[item.sourceType])}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                        style={{
                          backgroundColor: `color-mix(in srgb, ${statusConf.color} 15%, transparent)`,
                          color: statusConf.color,
                        }}
                      >
                        {item.status === 'processing' ? (
                          <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
                        ) : (
                          <span
                            className="inline-block h-1.5 w-1.5 rounded-full"
                            style={{ backgroundColor: statusConf.color }}
                          />
                        )}
                        {t(statusConf.labelKey)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums text-[var(--color-text-muted)]">
                      {toJstDateTime(item.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        title={t('common.archive')}
                        disabled={archiveMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveMutation.mutate(item.id);
                        }}
                        className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-secondary)]"
                      >
                        <Archive className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
